import React, { useState } from 'react'
import { SegmentedControl } from '../obsidian/SegmentedControl'
import type { ProcessingOptions } from '../../lib/pipeline'

export interface StudioPresetsBarProps {
  options: ProcessingOptions
  onChangeOptions: (newOptions: ProcessingOptions) => void
}

export type PresetKey = 'balanced' | 'max_savings' | 'ultra_fidelity' | 'custom'

const PRESETS: Record<Exclude<PresetKey, 'custom'>, ProcessingOptions> = {
  balanced: {
    quality: { avif: 50, webp: 80, jpeg: 82 },
    generateFavicons: true,
  },
  max_savings: {
    quality: { avif: 35, webp: 65, jpeg: 70 },
    generateFavicons: true,
  },
  ultra_fidelity: {
    quality: { avif: 70, webp: 90, jpeg: 92 },
    generateFavicons: true,
  },
}

export const StudioPresetsBar: React.FC<StudioPresetsBarProps> = ({
  options,
  onChangeOptions,
}) => {
  const [activePreset, setActivePreset] = useState<PresetKey>('balanced')
  const [showAdvanced, setShowAdvanced] = useState(false)

  const handlePresetChange = (presetId: string) => {
    const key = presetId as PresetKey
    setActivePreset(key)
    if (key !== 'custom') {
      onChangeOptions(PRESETS[key])
    }
  }

  const handleSliderChange = (
    codec: 'avif' | 'webp' | 'jpeg',
    value: number,
  ) => {
    setActivePreset('custom')
    onChangeOptions({
      ...options,
      quality: {
        ...options.quality,
        [codec]: value,
      },
    })
  }

  const handleToggleFavicons = () => {
    onChangeOptions({
      ...options,
      generateFavicons: !options.generateFavicons,
    })
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '18px 24px',
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        borderRadius: '20px',
        marginBottom: '32px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '14px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <i
            className="pi pi-sliders-h"
            style={{ color: 'var(--c-accent-electric, #38BFFF)', fontSize: '15px' }}
          />
          <span
            style={{
              fontFamily: "var(--font-display, 'Unbounded', sans-serif)",
              fontSize: '13px',
              fontWeight: 600,
              letterSpacing: '0.04em',
              color: '#ffffff',
              textTransform: 'uppercase',
            }}
          >
            Perfil de Calidad:
          </span>
        </div>

        <SegmentedControl
          value={activePreset}
          onChange={handlePresetChange}
          options={[
            { id: 'balanced', label: 'Equilibrado', badge: 'RECOMENDADO' },
            { id: 'max_savings', label: 'Máximo Ahorro' },
            { id: 'ultra_fidelity', label: 'Ultra Fidelidad' },
            { id: 'custom', label: 'Manual' },
          ]}
        />

        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'transparent',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.55)',
            fontSize: '13px',
            fontFamily: "var(--font-body, 'Plus Jakarta Sans', sans-serif)",
            cursor: 'pointer',
            padding: '6px 10px',
            borderRadius: '9999px',
            transition: 'all 150ms ease',
          }}
        >
          <span>{showAdvanced ? 'Ocultar ajuste fino' : 'Ajuste fino'}</span>
          <i className={`pi ${showAdvanced ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
        </button>
      </div>

      {showAdvanced && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          {/* AVIF */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12.5px',
                color: 'rgba(255, 255, 255, 0.75)',
              }}
            >
              <span>AVIF Calidad</span>
              <span className="tabular-nums" style={{ color: 'var(--c-accent-electric, #38BFFF)' }}>
                {options.quality.avif}%
              </span>
            </div>
            <input
              type="range"
              min="10"
              max="95"
              value={options.quality.avif}
              onChange={(e) => handleSliderChange('avif', Number(e.target.value))}
              style={{ accentColor: 'var(--c-accent-electric, #38BFFF)', cursor: 'pointer' }}
            />
          </div>

          {/* WebP */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12.5px',
                color: 'rgba(255, 255, 255, 0.75)',
              }}
            >
              <span>WebP Calidad</span>
              <span className="tabular-nums" style={{ color: 'var(--c-accent-electric, #38BFFF)' }}>
                {options.quality.webp}%
              </span>
            </div>
            <input
              type="range"
              min="20"
              max="98"
              value={options.quality.webp}
              onChange={(e) => handleSliderChange('webp', Number(e.target.value))}
              style={{ accentColor: 'var(--c-accent-electric, #38BFFF)', cursor: 'pointer' }}
            />
          </div>

          {/* JPEG */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12.5px',
                color: 'rgba(255, 255, 255, 0.75)',
              }}
            >
              <span>JPEG Calidad</span>
              <span className="tabular-nums" style={{ color: 'var(--c-accent-electric, #38BFFF)' }}>
                {options.quality.jpeg}%
              </span>
            </div>
            <input
              type="range"
              min="20"
              max="98"
              value={options.quality.jpeg}
              onChange={(e) => handleSliderChange('jpeg', Number(e.target.value))}
              style={{ accentColor: 'var(--c-accent-electric, #38BFFF)', cursor: 'pointer' }}
            />
          </div>

          {/* Favicons */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 8px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: '13px', color: '#ffffff', fontWeight: 600 }}>
                Favicon Suite
              </span>
              <span style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)' }}>
                Generar ICO/PNG para SVGs
              </span>
            </div>
            <button
              type="button"
              onClick={handleToggleFavicons}
              style={{
                width: '42px',
                height: '24px',
                borderRadius: '9999px',
                background: options.generateFavicons
                  ? 'var(--c-pulse, #2AE6B6)'
                  : 'rgba(255, 255, 255, 0.15)',
                border: 'none',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background 200ms ease',
              }}
            >
              <div
                style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: '#0B1230',
                  position: 'absolute',
                  top: '3px',
                  left: options.generateFavicons ? '21px' : '3px',
                  transition: 'left 200ms cubic-bezier(0.2, 0, 0, 1)',
                }}
              />
            </button>
          </div>

          {/* Video Codec */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <span style={{ fontSize: '12.5px', color: 'rgba(255, 255, 255, 0.75)' }}>
              Códec de Video
            </span>
            <select
              value={options.video?.codec || 'h264'}
              onChange={(e) => {
                setActivePreset('custom')
                onChangeOptions({
                  ...options,
                  video: {
                    codec: e.target.value as 'h264' | 'av1' | 'webm',
                    mode: options.video?.mode || 'auto',
                    maxDimension: options.video?.maxDimension || 1920,
                  },
                })
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                color: '#ffffff',
                padding: '6px 10px',
                borderRadius: '8px',
                fontSize: '12.5px',
                fontFamily: "var(--font-body, 'Plus Jakarta Sans', sans-serif)",
                cursor: 'pointer',
              }}
            >
              <option value="h264">H.264 FastStart (Universal 100%)</option>
              <option value="av1">SVT-AV1 (Máxima Compresión Cloud)</option>
              <option value="webm">WebM VP9 (Loop Web / Transparente)</option>
            </select>
          </div>

          {/* Video Routing Mode */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <span style={{ fontSize: '12.5px', color: 'rgba(255, 255, 255, 0.75)' }}>
              Motor de Video
            </span>
            <select
              value={options.video?.mode || 'auto'}
              onChange={(e) => {
                setActivePreset('custom')
                onChangeOptions({
                  ...options,
                  video: {
                    codec: options.video?.codec || 'h264',
                    mode: e.target.value as 'auto' | 'local' | 'cloud',
                    maxDimension: options.video?.maxDimension || 1920,
                  },
                })
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                color: '#ffffff',
                padding: '6px 10px',
                borderRadius: '8px',
                fontSize: '12.5px',
                fontFamily: "var(--font-body, 'Plus Jakarta Sans', sans-serif)",
                cursor: 'pointer',
              }}
            >
              <option value="auto">Auto (Híbrido Inteligente)</option>
              <option value="local">GPU Local (WebCodecs)</option>
              <option value="cloud">Cloud Run (FFmpeg 7)</option>
            </select>
          </div>
        </div>
      )}
    </div>
  )
}
